import React, { useEffect } from 'react'
import { NextPage, NextPageContext } from 'next'
import { DEFAULT_META_TAGS, MetaTagsInterface } from 'lib/routing/MetaTags'
import { MinimalLayout } from 'shared/layout/MinimalLayout'
import { useRouter } from 'next/router'
import { ApplicationLayout } from 'components/layout/ApplicationLayout'
import { createSearch, getInitialSectionId } from 'services/FionaAPI/Search/applicationHelpers'
import { ObjectFieldTemplate } from 'experiences/search/Application/JsonForm/templates/ObjectFieldTemplate'
import { addDDError, addDDTiming } from 'services/AnalyticsLite/Datadog/events'
import type { SearchesControllerCreateSearchRequest } from 'generated/api/apis/SearchesControllerApi'

interface SearchFlowOptions {
	prepopMode?: 'inline' | 'combined' | 'skip' | string
}

interface SearchProps {
	options: SearchFlowOptions
	formValues: SearchesControllerCreateSearchRequest
	metaTags: MetaTagsInterface
}

const ApplicationPage: NextPage<SearchProps> = (props: SearchProps): JSX.Element => {
	const router = useRouter()
	useEffect(() => {
		router.prefetch('/apply/[id]').finally(() => addDDTiming('prefetch_jsonform'))
	}, [router])
	useEffect(() => {
		createSearch(props.formValues)
			.catch(err => {
				addDDError(err)
				return createSearch()
			})
			.then(search => {
				const query = props.options.prepopMode === 'inline' ? { sectionId: getInitialSectionId(search)?.id } : {}
				router.replace({ pathname: '/apply/[id]', query: { id: search.id, ...query } }).catch(addDDError)
			})
			.catch(addDDError)
	}, [props.formValues, props.options.prepopMode, router])

	return (
		<MinimalLayout metaTags={props.metaTags}>
			<ApplicationLayout onBack={() => router.back()}>
				<ObjectFieldTemplate pending={true} />
			</ApplicationLayout>
		</MinimalLayout>
	)
}

ApplicationPage.getInitialProps = async (ctx: NextPageContext): Promise<SearchProps> => {
	const formValues = {
		productType: ctx.query.productType,
		loanPurpose: ctx.query.loanPurpose,
		loanAmount: typeof ctx.query.loanAmount === 'string' ? parseInt(ctx.query.loanAmount) : undefined,
		zipcode: ctx.query.zipcode,
		creditRating: ctx.query.creditRating
	} as SearchesControllerCreateSearchRequest
	return {
		options: { prepopMode: typeof ctx.query.prepopMode === 'string' ? ctx.query.prepopMode : undefined },
		formValues,
		metaTags: {
			...DEFAULT_META_TAGS,
			title: 'Your loan application with Fiona',
			description: 'The easiest way to compare personalized loan offers. Takes less than 60 seconds'
		}
	}
}
export default ApplicationPage
